import { useCallback } from 'react';
import { ResumeJourneyType } from './resumeJourney.type';
import { replacePlaceholders } from '@/core/utils/format.utils';
import { useGeneralContext } from '@/contexts/general/general.context';
import { useDashboard } from '@/contexts/dashboard/dashboard.context';
import { getDifferenceInDays } from '@/core/utils/policy.util';
import { INTERNAL_LOB, VEHICLE_TYPE } from '@/core/constants/policy.constants';

export const useResumeJourney = () => {
  const { dashboardLabels } = useDashboard();
  const { resumeJourneyCmsData } = dashboardLabels;

  const { policies: policiesData } = useGeneralContext();

  const getResumeNudges = useCallback(
    (expiryDateDifference: number) => {
      const policies = policiesData.value;
      if (!policies) return [];
      const allActivePolicies = Object.values(policies.active).reduce(
        (acc, curr) => [...acc, ...curr],
        []
      );
      const filteredPolicies = allActivePolicies.filter(
        (policy) =>
          policy.policyEndDate &&
          getDifferenceInDays(policy.policyEndDate) < expiryDateDifference &&
          getDifferenceInDays(policy.policyEndDate) > 0
      );

      let resumeNudges: ResumeJourneyType[] = [];
      filteredPolicies?.forEach((policy) => {
        if (policy.policyProduct?.portalLob === INTERNAL_LOB.motor) {
          resumeNudges.push({
            title: replacePlaceholders(
              resumeJourneyCmsData.motorExpiryCard.title,
              {
                vehicleModel: policy.vehicleModel
                  ? `${policy.vehicleModel}`
                  : 'your vehicle',
              }
            ),
            description: replacePlaceholders(
              resumeJourneyCmsData.motorExpiryCard.description,
              {
                days: policy.policyEndDate
                  ? getDifferenceInDays(policy.policyEndDate).toString()
                  : 'Few',
              }
            ),
            image:
              policy.vehicleImageURL ??
              (policy.vehicleClassification === VEHICLE_TYPE.twoWheeler
                ? '/images/dashboard/needsYourAttention/vehicle2w_placeholder.png'
                : '/images/dashboard/needsYourAttention/vehicle_placeholder.png'),
            internalLob: policy.policyProduct?.portalLob,
          });
        } else if (policy.policyProduct?.portalLob === INTERNAL_LOB.health) {
          resumeNudges.push({
            title: replacePlaceholders(
              resumeJourneyCmsData.healthExpiryCard.title,
              {
                days: policy.policyEndDate
                  ? getDifferenceInDays(policy.policyEndDate).toString()
                  : 'Few',
                policyHolderName: `${policy.policyHolderName}`,
              }
            ),
            description: resumeJourneyCmsData.healthExpiryCard.description,
            image: '/images/dashboard/needsYourAttention/expiry_resume.png',
            internalLob: policy.policyProduct?.portalLob,
          });
        }
      });

      return resumeNudges;
    },
    [policiesData.value, resumeJourneyCmsData]
  );

  return {
    getResumeNudges,
  };
};
